import { useState, type ReactNode } from "react";
import AdminAuditPanel from "./audit/AdminAuditPanel";
import AdminFeedbackPanel from "./feedback/AdminFeedbackPanel";
import AdminGigsPanel from "./gigs/AdminGigsPanel";
import AdminIssuesPanel from "./issues/AdminIssuesPanel";
import AdminNotificationsPanel from "./notifications/AdminNotificationsPanel";
import AdminPaymentsPanel from "./payments/AdminPaymentsPanel";
import AdminReviewsPanel from "./reviews/AdminReviewsPanel";
import AdminSectionCard from "./ui/AdminSectionCard";
import AdminUsersPanel from "./users/AdminUsersPanel";
import AdminSectionNav from "./ui/AdminSectionNav";
import type { AdminSectionId } from "./adminData";

const AdminDashboardHome = () => {
  const [activeSection, setActiveSection] = useState<AdminSectionId>("users");

  const sectionContent: Record<AdminSectionId, { eyebrow: string; title: string; description: string; body: ReactNode }> = {
    users: {
      eyebrow: "Users",
      title: "User administration",
      description: "Search, inspect, and support users without granting broad destructive powers too early.",
      body: <AdminUsersPanel />,
    },
    gigs: {
      eyebrow: "Gigs",
      title: "Gig administration",
      description: "Review platform activity, inspect gig records, and prepare moderation tools for later phases.",
      body: <AdminGigsPanel />,
    },
    reviews: {
      eyebrow: "Reviews",
      title: "Review administration",
      description: "Browse submitted reviews with attached gig, request, reviewer, reviewee, and participant context.",
      body: <AdminReviewsPanel />,
    },
    feedback: {
      eyebrow: "Feedback",
      title: "Feedback administration",
      description: "Review website feedback from completed gigs with poster, teen, rating, and gig context.",
      body: <AdminFeedbackPanel />,
    },
    issues: {
      eyebrow: "Issues",
      title: "Issue review",
      description: "Review payout-blocking gig issues, inspect messages, and resolve disputes with a clear audit trail.",
      body: <AdminIssuesPanel />,
    },
    payments: {
      eyebrow: "Payments",
      title: "Payment operations",
      description: "Search paid gig money movement, inspect Stripe references, and take audited manual payout or refund actions.",
      body: <AdminPaymentsPanel />,
    },
    notifications: {
      eyebrow: "Notifications",
      title: "Notification operations",
      description: "Understand delivery failures, monitor the outbox, and support safe resend/requeue workflows.",
      body: <AdminNotificationsPanel />,
    },
    audit: {
      eyebrow: "Audit",
      title: "Admin audit history",
      description: "Every meaningful admin action should be captured so support and operational changes are explainable later.",
      body: <AdminAuditPanel />,
    },
  };

  const currentSection = sectionContent[activeSection];

  return (
    <div className="space-y-4">
      <AdminSectionNav activeSection={activeSection} onSelect={setActiveSection} />
      <AdminSectionCard
        eyebrow={currentSection.eyebrow}
        title={currentSection.title}
        description={currentSection.description}
      >
        {currentSection.body}
      </AdminSectionCard>
    </div>
  );
};

export default AdminDashboardHome;
